'use client'

import { useState } from 'react'
import Image from 'next/image'
import type { ProductImage } from '@/lib/types'

interface ProductImageGalleryProps {
  images: ProductImage[]
}

export default function ProductImageGallery({ images }: ProductImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  if (images.length === 0) {
    return (
      <div className="aspect-square bg-cloud rounded-3xl flex items-center justify-center shadow-soft">
        <span className="text-gray-500">No image available</span>
      </div>
    )
  }

  const selectedImage = images[selectedIndex]
  const toImageUrl = (path: string) =>
    path.startsWith('https://')
      ? path
      : `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/product-images/${path}`
  const imageUrl = toImageUrl(selectedImage.path)

  return (
    <div className="space-y-4">
      <div className="aspect-square relative bg-cloud rounded-3xl overflow-hidden shadow-soft">
        <Image
          src={imageUrl}
          alt={selectedImage.alt || 'Product image'}
          fill
          className="object-cover"
          priority
        />
      </div>

      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-3">
          {images.map((image, index) => {
            const thumbUrl = toImageUrl(image.path)
            return (
              <button
                key={image.id}
                onClick={() => setSelectedIndex(index)}
                className={`aspect-square relative bg-cloud rounded-2xl overflow-hidden border-2 transition-colors ${
                  index === selectedIndex ? 'border-primary' : 'border-transparent'
                }`}
              >
                <Image
                  src={thumbUrl}
                  alt={image.alt || 'Product thumbnail'}
                  fill
                  className="object-cover"
                />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
